import { Funnel, TravelWalletOffer } from "redmond";

// ─── Offer config registry ────────────────────────────────────────────────────
// To add a new offer type:
//   1. Add an entry here — checkoutUnlockMessage and ineligibleAtCheckout are
//      the only fields callers depend on.
//   That's it — no other files need to change.

interface TravelOfferConfig {
  offerType: TravelOfferType;
  /** Whether to show this offer as ineligible (but visible) in checkout */
  ineligibleAtCheckout: boolean;
  /**
   * HTML string shown below the offer row when it cannot yet be applied.
   * Args: (rewardFormatted, moreFormatted, rewardAmount)
   */
  /* eslint-disable no-unused-vars -- function type params are documentation only */
  checkoutUnlockMessage: (
    rewardFormatted: string,
    moreFormatted: string,
    rewardAmount: number
  ) => string;
  /* eslint-enable no-unused-vars */
}

const TRAVEL_OFFER_REGISTRY = [
  {
    offerType: "FTB_FLAT_OFF",
    ineligibleAtCheckout: true,
    checkoutUnlockMessage: (reward: string, more: string) =>
      `Unlock your <strong>${reward} off</strong> when you spend <strong>${more} more</strong> on your hotel booking.`,
  },
  {
    offerType: "FTB_PERC_TC",
    ineligibleAtCheckout: false,
    checkoutUnlockMessage: (reward: string, more: string, rewardAmount: number) =>
      `Unlock your <strong>${rewardAmount}% or up to ${reward} travel credit</strong> when you spend <strong>${more} more</strong> on your hotel booking.`,
  },
  {
    offerType: "FTB_EARN_TC",
    ineligibleAtCheckout: true,
    checkoutUnlockMessage: (reward: string, more: string) =>
      `Unlock your <strong>${reward} travel credit</strong> when you spend <strong>${more} more</strong> on your hotel booking.`,
  },
  {
    offerType: "FB_FLAT_OFF",
    ineligibleAtCheckout: true,
    checkoutUnlockMessage: (reward: string, more: string) =>
      `Unlock your <strong>${reward} off</strong> when you spend <strong>${more} more</strong> on your hotel booking.`,
  },
  {
    offerType: "FALL_FLAT_OFF",
    ineligibleAtCheckout: true,
    checkoutUnlockMessage: (reward: string, more: string) =>
      `Unlock your <strong>${reward} off</strong> when you spend <strong>${more} more</strong> on your booking.`,
  },
] as const satisfies ReadonlyArray<Omit<TravelOfferConfig, "offerType"> & { offerType: string }>;

export type TravelOfferType = (typeof TRAVEL_OFFER_REGISTRY)[number]["offerType"];

export const TRAVEL_OFFER_CONFIGS = new Map(
  TRAVEL_OFFER_REGISTRY.map((c) => [c.offerType, c])
) as Map<TravelOfferType, TravelOfferConfig>;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TravelOfferData {
  type: TravelOfferType;
  rewardAmount: number;
  minSpend: number;
}

// ─── Utils ────────────────────────────────────────────────────────────────────

const TRAVEL_OFFER_PREFIXES = [
  "OFFER_TYPE=FTB_",
  "OFFER_TYPE=FB_",
  "OFFER_TYPE=FALL_",
] as const;

type OfferWithDescriptions = Pick<TravelWalletOffer, "descriptions">;
type OfferWithId = Pick<TravelWalletOffer, "id" | "descriptions" | "funnels">;

export const getTravelOfferData = (
  offer?: OfferWithDescriptions
): TravelOfferData | undefined => {
  const desc = offer?.descriptions.find((d) =>
    TRAVEL_OFFER_PREFIXES.some((p) => d.startsWith(p))
  );
  if (!desc) return undefined;
  const value = desc.slice("OFFER_TYPE=".length);
  const [type, rewardStr, minSpendStr] = value.split("|");
  if (
    !TRAVEL_OFFER_CONFIGS.has(type as TravelOfferType) ||
    rewardStr === undefined ||
    minSpendStr === undefined
  )
    return undefined;
  return {
    type: type as TravelOfferType,
    rewardAmount: parseFloat(rewardStr),
    minSpend: parseFloat(minSpendStr),
  };
};

export const isTravelOffer = (offer?: OfferWithDescriptions): boolean =>
  !!getTravelOfferData(offer);

export const isIneligibleTravelOffer = (
  offer?: OfferWithDescriptions
): boolean => {
  const data = getTravelOfferData(offer);
  if (!data) return false;
  return TRAVEL_OFFER_CONFIGS.get(data.type)?.ineligibleAtCheckout ?? false;
};

export const isTravelOfferForFunnel = (
  offer: OfferWithDescriptions & Pick<TravelWalletOffer, "funnels">,
  currentFunnel: Funnel
): boolean => isTravelOffer(offer) && offer.funnels.includes(currentFunnel);

export const findIneligibleTravelOffers = <T extends OfferWithId>(
  allWalletOffers: T[] = [],
  applicableOffers: OfferWithId[] = [],
  currentFunnel?: Funnel
): T[] => {
  const applicableIds = new Set(applicableOffers.map((o) => o.id));
  return allWalletOffers.filter(
    (o) =>
      isIneligibleTravelOffer(o) &&
      !applicableIds.has(o.id) &&
      (currentFunnel == null || o.funnels.includes(currentFunnel))
  );
};

export const hasIneligibleTravelOffer = (
  allWalletOffers: OfferWithId[] = [],
  applicableOffers: OfferWithId[] = [],
  currentFunnel?: Funnel
): boolean =>
  findIneligibleTravelOffers(allWalletOffers, applicableOffers, currentFunnel).length > 0;
